import React, { lazy, Suspense, useState, useRef, useEffect, useContext } from "react";
import "./CreateAgendaEventModal.css";
import { format } from "date-fns";
import moment from "moment";
import { toast } from "react-toastify";
import ModernDatePicker from "../common/ModernDatePicker";
import ModernTimePicker from "../common/ModernTimePicker";
import {
  LuX,
  LuCalendarPlus,
  LuVideo,
  LuStethoscope,
  LuBookmark,
  LuCalendar,
  LuClock,
  LuChevronDown,
  LuEye,
  LuEyeOff,
  LuInfo,
  LuPlus,
  LuTrash2,
  LuCheck,
  LuGlobe,
  LuLock,
  LuRepeat,
  LuMapPin,
  LuLoader2,
  LuGripVertical,
  LuMoreVertical,
  LuPencil,
  LuMaximize2,
} from "react-icons/lu";
import { TbPhotoUp } from "react-icons/tb";
import axios from "axios";
import {
  adminBaseURL,
  AddEventApi,
  EditEventApi,
  EditPostApi,
  _imagesURL,
} from "../../constants/API";
import { authorizeMultipart } from "../../shared/authorize";
import {
  createAgendaEventQuestions,
  normalizeAgendaQuestionsForForm,
  syncAgendaEventQuestions,
} from "../../services/agendaQuestionApi";
import ImageCropModal from "../common/ImageCropModal";
import ImagePreviewModal from "../common/ImagePreviewModal";
import ModalAddLocation from "../common/ModalAddLocation";
import { useCustomToast } from "../common/CustomToast";
import { Language } from "../../App";
import {
  AVAILABILITY_OPTIONS,
  RECURRENCE_OPTIONS,
  getQuestionTypeLabel,
} from "../../constants/agendaConstants";
import { resolveAgendaImageUrl, releaseAgendaScrollLock } from "../../utils/agendaUtils";
import { extractApiErrorMessage } from "../../utils/apiUtils";
import {
  categorizeFiles,
  readFileAsDataUrl,
  createDropzoneHandlers,
} from "../../utils/fileDropUtils";


const AddQuestionModal = lazy(() => import("./AddQuestionModal"));

const tryParseLatLng = (val) => {
  if (!val) return null;
  if (typeof val === "object") {
    const lat = Number(val.lat);
    const lng = Number(val.lng);
    if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
  }
  try {
    const parsed = JSON.parse(val);
    if (parsed && typeof parsed === "object") {
      const lat = Number(parsed.lat);
      const lng = Number(parsed.lng);
      if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
    }
  } catch { }
  if (typeof val === "string" && val.includes(",")) {
    const [lat, lng] = val.split(",").map(Number);
    if (!isNaN(lat) && !isNaN(lng)) return { lat, lng };
  }
  return null;
};

const getOptionalFormValue = (value) => {
  if (value === null || value === undefined) return "";

  const normalized = String(value).trim();
  return ["@empty_data_", "undefined", "null"].includes(
    normalized.toLowerCase()
  )
    ? ""
    : normalized;
};

const getOptionalAudienceLimitValue = (...values) => {
  for (const value of values) {
    const normalized = getOptionalFormValue(value);
    if (!normalized) continue;

    const numericValue = Number(normalized);
    if (Number.isInteger(numericValue) && numericValue > 0) {
      return String(numericValue);
    }
  }

  return "";
};

const isValidAudienceLimit = (value) => {
  const normalized = String(value ?? "").trim();
  if (!normalized) return true;

  const numericValue = Number(normalized);
  return Number.isInteger(numericValue) && numericValue > 0;
};

const getResponseScheduleId = (response, fallbackId = null) => {
  const responseData = response?.data || {};
  const responseItem =
    responseData.post || responseData.data?.post || responseData.data || {};

  return (
    responseItem?.id ||
    responseData.event?.id ||
    responseData.event_id ||
    responseData.post_id ||
    responseData.data?.event?.id ||
    responseData.data?.event_id ||
    responseData.data?.post_id ||
    responseData.id ||
    fallbackId
  );
};



export default function CreateAgendaEventModal({
  isOpen,
  toggle,
  onAddEvent,
  editEvent = null,
  isLoadingDetails = false,
  onUpdateEvent,
  onInviteSchedule,
  hideTopCards = false,
  hideMentorshipNotice = false,
  allowedTypes = ["event", "webinar", "clinical_session"],
  initialType = "event",
}) {
  const { lang } = useContext(Language);
  const { showToast } = useCustomToast();

  // Active Schedule Type: "event", "webinar", "clinical_session"
  const [scheduleType, setScheduleType] = useState(initialType || "event");
  const requiresBanner = scheduleType === "event" || scheduleType === "webinar";

  // Load subcategories from splash data if available
  const SplashData = JSON.parse(localStorage.getItem("SplashData") || "{}");
  const splashSubcategories = SplashData?.home?.subcategories || [];
  const categoryOptions = splashSubcategories.map((item) => item.name)


  // Form State Values
  const [formData, setFormData] = useState({
    title: "",
    organizerName: "",
    // Category/dates/times start empty so their "Select Category" /
    // "YYYY-MM-DD" / "HH:mm" placeholders actually show on a fresh form,
    // instead of being silently pre-filled with today/a computed time.
    category: "",
    startDate: "",
    endDate: "",
    startTime: "",
    endTime: "",
    duration: "60",
    location: "",
    address: "",
    website: "",
    registrationLink: "",
    availability: "Online",
    visibility: "Public",
    recurrence: "One Time",
    audienceLimit: "",
    password: "",
    price: "",
    currency: "EUR",
    description: "",
    lat_lng: null,
  });

  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [bannerPreview, setBannerPreview] = useState(null);
  const [bannerDragActive, setBannerDragActive] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [initialQuestions, setInitialQuestions] = useState([]);
  const [draggedQuestionIndex, setDraggedQuestionIndex] = useState(null);
  const [dragOverQuestionIndex, setDragOverQuestionIndex] = useState(null);
  const [addQuestionModalOpen, setAddQuestionModalOpen] = useState(false);
  const [editingQuestionIndex, setEditingQuestionIndex] = useState(null);
  const [activeQuestionMenuIndex, setActiveQuestionMenuIndex] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Location Picker State
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [isLocationLoading, setIsLocationLoading] = useState(false);
  const [userCoords, setUserCoords] = useState(null);
  const [locationData, setLocationData] = useState(null);

  // Image Cropper State (Fixed 16:9 for Event & Webinar)
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState(null);
  const fileInputRef = useRef(null);

  // Full image preview modal state
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const togglePreview = () => setIsPreviewOpen(!isPreviewOpen);

  // Slide-in animation state on mount/open (ensures silky smooth right-to-left slide-in)
  const [slideIn, setSlideIn] = useState(false);
  const hasOpenedRef = useRef(false);

  useEffect(() => {
    if (isOpen) {
      hasOpenedRef.current = true;
      let raf2;
      const raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => setSlideIn(true));
      });
      return () => {
        cancelAnimationFrame(raf1);
        if (raf2) cancelAnimationFrame(raf2);
      };
    } else {
      setSlideIn(false);
    }
  }, [isOpen]);

  const isClosing = !isOpen && hasOpenedRef.current;

  // Shake animation state for backdrop click in create mode (matching CreateMentorship UX)
  const [shake, setShake] = useState(false);
  const shakeTimeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (shakeTimeoutRef.current) clearTimeout(shakeTimeoutRef.current);
    };
  }, []);

  const handleBackdropClick = () => {
    if (editEvent) {
      toggle();
      return;
    }
    setShake(true);
    if (shakeTimeoutRef.current) clearTimeout(shakeTimeoutRef.current);
    shakeTimeoutRef.current = setTimeout(() => {
      shakeTimeoutRef.current = null;
      setShake(false);
    }, 450);
  };

  // Category dropdown state
  const [categoryOpen, setCategoryOpen] = useState(false);
  const categoryRef = useRef(null);

  // Availability dropdown state
  const [availabilityOpen, setAvailabilityOpen] = useState(false);
  const availabilityRef = useRef(null);

  // Location is optional for all events
  const isLocationRequired = false;

  // Location is only editable once coordinates (lat/lng) are extracted from the map and location is not empty
  const hasLocationCoords = Boolean(tryParseLatLng(formData.lat_lng) && formData.location?.trim());

  // Close dropdowns & question menu on outside click
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (categoryRef.current && !categoryRef.current.contains(e.target)) {
        setCategoryOpen(false);
      }
      if (availabilityRef.current && !availabilityRef.current.contains(e.target)) {
        setAvailabilityOpen(false);
      }
      if (!e.target.closest || !e.target.closest(".agenda-q-menu-wrap")) {
        setActiveQuestionMenuIndex(null);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  // Handle ESC key to close drawer
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isOpen) {
        toggle();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, toggle]);

  // Keep the page locked for the lifetime of the drawer only. This must stay
  // separate from the form pre-fill effect below: editing an event replaces
  // `editEvent` after its detail request finishes, which should not release
  // the page scroll while the drawer is still open.
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      return () => {
        releaseAgendaScrollLock(true);
      };
    } else {
      releaseAgendaScrollLock(true);
    }
  }, [isOpen]);

  // Reset / pre-fill state when the drawer opens.
  useEffect(() => {
    if (isOpen) {
      const defaultStartM = moment().add(15, "minutes").startOf("hour").add(1, "hour");

      if (editEvent) {
        // Pre-fill for Edit Mode
        const rawType = String(
          editEvent.type ||
          editEvent.entity_type ||
          editEvent.category ||
          editEvent.raw?.entity_type ||
          editEvent.raw?.type ||
          ""
        ).toLowerCase().replace(/_/g, "-");

        const mappedType =
          rawType.includes("clinical") || rawType.includes("session")
            ? "clinical_session"
            : rawType.includes("webinar")
              ? "webinar"
              : "event";
        setScheduleType(mappedType);

        let matchedCategory = "";
        const rawCategoryIdentifier =
          editEvent.category ||
          editEvent.subtitle ||
          editEvent.subcategory_name ||
          editEvent.raw?.subcategory_name ||
          editEvent.raw?.subcategory?.name ||
          editEvent.raw?.subcategory_id ||
          editEvent.subcategory_id;

        if (rawCategoryIdentifier) {
          const foundSub = splashSubcategories.find(
            (s) =>
              String(s.id) === String(rawCategoryIdentifier) ||
              String(s.name || "").toLowerCase() === String(rawCategoryIdentifier).toLowerCase() ||
              String(s.title || "").toLowerCase() === String(rawCategoryIdentifier).toLowerCase()
          );
          if (foundSub) {
            matchedCategory = foundSub.name || foundSub.title;
          } else {
            const directMatch = categoryOptions.find(
              (opt) => String(opt).toLowerCase() === String(rawCategoryIdentifier).toLowerCase()
            );
            matchedCategory = directMatch || String(rawCategoryIdentifier);
          }
        }
        if (!matchedCategory && categoryOptions.length > 0) {
          matchedCategory = categoryOptions[0];
        }

        const bannerCandidate =
          editEvent.cover_image ||
          editEvent.cover ||
          editEvent.cover_img ||
          editEvent.coverImage ||
          editEvent.image ||
          editEvent.thumbnail ||
          editEvent.media_url ||
          editEvent.raw?.cover_image ||
          editEvent.raw?.cover ||
          editEvent.raw?.cover_img ||
          editEvent.raw?.coverImage ||
          editEvent.raw?.image ||
          editEvent.raw?.thumbnail ||
          editEvent.raw?.media_url ||
          (Array.isArray(editEvent.media) ? editEvent.media[0] : editEvent.media) ||
          (Array.isArray(editEvent.images) ? editEvent.images[0] : editEvent.images) ||
          (Array.isArray(editEvent.raw?.media) ? editEvent.raw?.media[0] : editEvent.raw?.media) ||
          (Array.isArray(editEvent.raw?.images) ? editEvent.raw?.images[0] : editEvent.raw?.images);

        const resolvedBanner = resolveAgendaImageUrl(bannerCandidate, "");
        const isDefaultImg =
          typeof resolvedBanner === "string" &&
          (resolvedBanner.includes("default_agenda") || resolvedBanner.includes("default_avatar"));
        const initialBanner = !isDefaultImg && resolvedBanner ? resolvedBanner : null;

        const initialLatLng = tryParseLatLng(editEvent.lat_lng || editEvent.raw?.lat_lng);
        const initialLocation = getOptionalFormValue(
          editEvent.location || editEvent.raw?.location || editEvent.address || editEvent.raw?.address
        );

        // Normalize Start Date
        const initialStartDate =
          editEvent.startDate ||
          editEvent.date ||
          editEvent.schedule_date ||
          editEvent.start_date ||
          editEvent.raw?.schedule_date ||
          editEvent.raw?.start_date ||
          format(new Date(), "yyyy-MM-dd");

        // Normalize End Date
        const initialEndDate =
          editEvent.endDate ||
          editEvent.end_date ||
          editEvent.raw?.end_date ||
          initialStartDate;

        // Normalize Start Time
        let initialStartTime =
          editEvent.startTime ||
          editEvent.start_time ||
          editEvent.schedule_time ||
          editEvent.raw?.schedule_time ||
          editEvent.raw?.start_time ||
          "";
        if (!initialStartTime && editEvent.time) {
          const parts = String(editEvent.time).split("-");
          initialStartTime = parts[0]?.trim();
        }
        if (initialStartTime) {
          const parsedST = moment(initialStartTime, [
            "HH:mm",
            "HH:mm:ss",
            "hh:mm A",
            "h:mm A",
            "hh:mm a",
            "h:mm a",
            "YYYY-MM-DD HH:mm:ss",
            "YYYY-MM-DDTHH:mm:ss",
          ]);
          if (parsedST.isValid()) {
            initialStartTime = parsedST.format("HH:mm");
          }
        } else {
          initialStartTime = "";
        }

        // Normalize End Time
        let initialEndTime =
          editEvent.endTime ||
          editEvent.end_time ||
          editEvent.raw?.end_time ||
          "";
        if (!initialEndTime && editEvent.time && String(editEvent.time).includes("-")) {
          const parts = String(editEvent.time).split("-");
          initialEndTime = parts[1]?.trim();
        }
        if (initialEndTime) {
          const parsedET = moment(initialEndTime, [
            "HH:mm",
            "HH:mm:ss",
            "hh:mm A",
            "h:mm A",
            "hh:mm a",
            "h:mm a",
            "YYYY-MM-DD HH:mm:ss",
            "YYYY-MM-DDTHH:mm:ss",
          ]);
          if (parsedET.isValid()) {
            initialEndTime = parsedET.format("HH:mm");
          }
        } else {
          initialEndTime = "";
        }

        const initialDuration = String(
          editEvent.duration ||
          editEvent.time_limit ||
          editEvent.limit ||
          editEvent.raw?.duration ||
          editEvent.raw?.time_limit ||
          "60"
        );

        const initialDescription =
          editEvent.description ||
          editEvent.caption ||
          editEvent.details ||
          editEvent.content ||
          editEvent.raw?.caption ||
          editEvent.raw?.description ||
          editEvent.raw?.details ||
          "";

        const initialTitle =
          editEvent.title ||
          editEvent.name ||
          editEvent.topic ||
          editEvent.raw?.title ||
          editEvent.raw?.name ||
          "";

        const initialHost =
          editEvent.host ||
          editEvent.organizer ||
          editEvent.raw?.organizer ||
          editEvent.raw?.doctor_name ||
          editEvent.raw?.user?.name ||
          editEvent.raw?.user?.user_name ||
          "";

        const rawRecurrence =
          editEvent.recurrence ||
          editEvent.visibility ||
          editEvent.raw?.recurrence ||
          editEvent.raw?.visibility ||
          "One Time";
        const normalizedRecurrence =
          ["daily", "weekly", "monthly"].includes(String(rawRecurrence).toLowerCase())
            ? rawRecurrence.charAt(0).toUpperCase() + rawRecurrence.slice(1).toLowerCase()
            : "One Time";

        setFormData({
          title: initialTitle,
          organizerName: initialHost,
          category: matchedCategory,
          startDate: initialStartDate,
          endDate: initialEndDate,
          startTime: initialStartTime,
          endTime: initialEndTime,
          duration: initialDuration,
          location: initialLocation,
          address: initialLocation,
          city: editEvent.city || editEvent.raw?.city || "",
          country: editEvent.country || editEvent.raw?.country || "",
          short_location: editEvent.short_location || editEvent.shortLocation || editEvent.raw?.short_location || "",
          website: getOptionalFormValue(editEvent.website || editEvent.raw?.website),
          availability: (() => {
            const rawAvail = editEvent.availability;
            if (rawAvail === "In-Person" || rawAvail === "Offline") return "Offline";
            if (rawAvail === "Hybrid" || rawAvail === "Online & Offline") return "Online & Offline";
            if (rawAvail === "Online") return "Online";
            return editEvent.isOnline ? "Online" : "Offline";
          })(),
          recurrence: normalizedRecurrence,
          audienceLimit: getOptionalAudienceLimitValue(
            editEvent.audienceLimit,
            editEvent.maxAttendees,
            editEvent.raw?.audience_limit,
            editEvent.raw?.audienceLimit,
            editEvent.raw?.limit,
            editEvent.limit
          ),
          password: getOptionalFormValue(editEvent.password || editEvent.raw?.password),
          price: (() => {
            const rawPriceVal = editEvent.priceValue || editEvent.price || editEvent.raw?.price;
            if (!rawPriceVal) return "";
            const priceStr = String(rawPriceVal).trim();
            if (
              priceStr.toLowerCase() === "free" ||
              priceStr === "0" ||
              priceStr === "@empty_data_"
            ) {
              return "";
            }
            const cleaned = priceStr.replace(/[^0-9]/g, "");
            const intVal = parseInt(cleaned, 10);
            return isNaN(intVal) || intVal <= 0 ? "" : String(intVal);
          })(),
          currency: "EUR",
          description: initialDescription,
          lat_lng: initialLatLng,
        });

        setBannerPreview(initialBanner);
        setCropImageSrc(null);

        const loadedQuestions = normalizeAgendaQuestionsForForm(editEvent.questions || editEvent.raw?.questions);
        setQuestions(loadedQuestions);
        setInitialQuestions(loadedQuestions);
        setAddQuestionModalOpen(false);
        setErrors({});
        setLocationData(
          initialLatLng
            ? {
              lat: initialLatLng.lat,
              lng: initialLatLng.lng,
              displayText: initialLocation,
              city: editEvent.city || editEvent.raw?.city || "",
              country: editEvent.country || editEvent.raw?.country || "",
              short_location: editEvent.short_location || editEvent.shortLocation || editEvent.raw?.short_location || "",
            }
            : null
        );
        setUserCoords(
          initialLatLng
            ? { lat: initialLatLng.lat, lng: initialLatLng.lng }
            : null
        );
        setIsLocationLoading(false);
        setLocationModalOpen(false);
      } else {
        // Fresh reset for Create Mode — category/dates/times stay empty so
        // their placeholders ("Select Category", "YYYY-MM-DD", "HH:mm") show
        // instead of a silently pre-filled default the user never chose.
        resetForm(initialType || "event");
      }
    }
  }, [isOpen, editEvent, initialType]);

  const resetForm = (targetType = initialType || "event") => {
    setFormData({
      title: "",
      organizerName: "",
      category: "",
      startDate: "",
      endDate: "",
      startTime: "",
      endTime: "",
      duration: "60",
      location: "",
      address: "",
      website: "",
      registrationLink: "",
      availability: "Online",
      visibility: "Public",
      recurrence: "One Time",
      audienceLimit: "",
      password: "",
      price: "",
      currency: "EUR",
      description: "",
      lat_lng: null,
    });
    setBannerPreview(null);
    setCropImageSrc(null);
    setQuestions([]);
    setInitialQuestions([]);
    setAddQuestionModalOpen(false);
    setErrors({});
    setScheduleType(targetType);
    setLocationData(null);
    setUserCoords(null);
    setIsLocationLoading(false);
    setLocationModalOpen(false);
    setCategoryOpen(false);
    setAvailabilityOpen(false);
    setActiveQuestionMenuIndex(null);
    setBannerDragActive(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleLocationClick = () => {
    if (isLocationLoading) return;

    const hasValidLocation = Boolean(formData.location?.trim());

    if (hasValidLocation && locationData?.lat && locationData?.lng) {
      setLocationModalOpen(true);
      return;
    }

    const parsedCoords = hasValidLocation ? tryParseLatLng(formData.lat_lng) : null;
    if (parsedCoords) {
      setUserCoords(parsedCoords);
      setLocationModalOpen(true);
      return;
    }

    if (!navigator.geolocation) {
      setLocationModalOpen(true);
      return;
    }

    setIsLocationLoading(true);

    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        setUserCoords({ lat: coords.latitude, lng: coords.longitude });
        setIsLocationLoading(false);
        setLocationModalOpen(true);
      },
      (err) => {
        console.warn("Geolocation:", err);
        setIsLocationLoading(false);
        setLocationModalOpen(true);
      },
      { timeout: 6000 }
    );
  };

  const handleSaveLocation = (loc) => {
    const cityCountry = [loc.city, loc.country].filter(Boolean).join(", ");
    const fullLocation = loc.address || loc.displayText || cityCountry || "";

    setFormData((prev) => ({
      ...prev,
      location: fullLocation,
      address: fullLocation,
      city: loc.city || "",
      country: loc.country || "",
      short_location: cityCountry,
      lat_lng: { lat: loc.lat, lng: loc.lng },
    }));
    setLocationData({
      ...loc,
      city: loc.city || "",
      country: loc.country || "",
      short_location: cityCountry,
      displayText: fullLocation,
      address: fullLocation,
    });
    setLocationModalOpen(false);
    setErrors((prev) => {
      const next = { ...prev };
      if (fullLocation) {
        delete next.location;
        delete next.address;
      }
      return next;
    });
  };

  const getShortLocation = (locStr, locData, isOnline) => {
    if (isOnline) return "Online";
    if (!locStr) return "";

    // 1. If explicit city & country from map modal or state
    const c = locData?.city || formData?.city;
    const co = locData?.country || formData?.country;
    if (c && co) {
      return `${c.trim()}, ${co.trim()}`;
    }
    if (c || co) {
      return (c || co).trim();
    }

    if (locData?.short_location || formData?.short_location) {
      return locData?.short_location || formData?.short_location;
    }

    // 2. Parse City, Country from comma-separated location string
    // e.g. "Hospital La Paz, Paseo de la Castellana 261, Madrid, Spain" -> "Madrid, Spain"
    const segments = String(locStr)
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    if (segments.length >= 2) {
      return `${segments[segments.length - 2]}, ${segments[segments.length - 1]}`;
    }

    if (segments.length === 1) {
      return segments[0];
    }

    return "";
  };

  const handleClearLocation = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setFormData((prev) => ({
      ...prev,
      location: "",
      address: "",
      city: "",
      country: "",
      short_location: "",
      lat_lng: null,
    }));
    setLocationData(null);
    setUserCoords(null);
    setErrors((prev) => {
      const next = { ...prev };
      delete next.location;
      delete next.address;
      return next;
    });
  };
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "price") {
      const digitsOnly = value.replace(/\D/g, "");
      setFormData((prev) => ({
        ...prev,
        price: digitsOnly,
      }));
      if (errors[name]) {
        setErrors((prev) => ({ ...prev, [name]: "" }));
      }
      return;
    }
    if (name === "agendaPassword" || name === "password") {
      setFormData((prev) => ({
        ...prev,
        password: value,
      }));
      if (errors[name] || errors.password) {
        setErrors((prev) => ({ ...prev, [name]: "", password: "" }));
      }
      return;
    }
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const getTimeMoment = (timeStr, dateStr) => {
    if (!timeStr) return null;
    const timeM = moment(timeStr, [
      "HH:mm",
      "HH:mm:ss",
      "hh:mm A",
      "h:mm A",
      "hh:mm a",
      "h:mm a",
      "YYYY-MM-DD HH:mm:ss",
      "YYYY-MM-DDTHH:mm:ss",
    ]);
    if (!timeM.isValid()) return null;
    const targetDate = dateStr && moment(dateStr).isValid() ? moment(dateStr) : moment();
    return targetDate.hour(timeM.hour()).minute(timeM.minute()).second(0);
  };

  // Dynamic minTime helpers for Start Time and End Time
  const isStartToday =
    Boolean(formData.startDate) &&
    moment(formData.startDate).isSame(moment(), "day");
  const minStartTime = isStartToday ? moment().add(5, "minutes") : undefined;

  const isSameDay =
    Boolean(formData.startDate) &&
    Boolean(formData.endDate) &&
    moment(formData.startDate).isSame(moment(formData.endDate), "day");
  const isEndToday =
    Boolean(formData.endDate) &&
    moment(formData.endDate).isSame(moment(), "day");

  const startMomentForMin = formData.startTime
    ? moment(formData.startTime, ["HH:mm", "hh:mm A", "HH:mm:ss"])
    : null;

  const minEndTime = isSameDay
    ? startMomentForMin && startMomentForMin.isValid()
      ? startMomentForMin.clone().add(5, "minutes")
      : isEndToday
        ? moment().add(10, "minutes")
        : undefined
    : isEndToday
      ? moment().add(5, "minutes")
      : undefined;

  const handleDisabledFieldClick = (field) => {
    if (field === "start_time" || field === "end_date") {
      showToast(lang?.select_start_date_first || "Select start date first!", "error");
      toast.error(lang?.select_start_date_first || "Select start date first!");
    } else if (field === "end_time") {
      if (!formData.startDate) {
        showToast(lang?.select_start_date_first || "Select start date first!", "error");
        toast.error(lang?.select_start_date_first || "Select start date first!");
      } else {
        showToast(lang?.select_end_date_first || "Select end date first!", "error");
        toast.error(lang?.select_end_date_first || "Select end date first!");
      }
    }
  };

  const handleDateChange = (val, fieldName) => {
    const formatted = val && moment(val).isValid() ? moment(val).format("YYYY-MM-DD") : "";
    setFormData((prev) => {
      const updated = { ...prev, [fieldName]: formatted };

      if (fieldName === "startDate") {
        if (prev.endDate && formatted && moment(prev.endDate).isBefore(moment(formatted), "day")) {
          updated.endDate = "";
        }
      }

      return updated;
    });

    if (errors[fieldName]) {
      setErrors((prev) => ({ ...prev, [fieldName]: "" }));
    }
  };

  const handleTimeChange = (val, fieldName) => {
    const formatted = val && moment(val).isValid() ? moment(val).format("HH:mm") : "";
    setFormData((prev) => ({
      ...prev,
      [fieldName]: formatted,
    }));

    if (errors[fieldName]) {
      setErrors((prev) => ({ ...prev, [fieldName]: "" }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    if (name === "location" && (!value || !value.toString().trim())) {
      setFormData((prev) => ({
        ...prev,
        location: "",
        address: "",
        city: "",
        country: "",
        short_location: "",
        lat_lng: null,
      }));
      setLocationData(null);
      setUserCoords(null);
    }
    if (!value || !value.toString().trim()) {
      if (name === "title" || name === "description") {
        setErrors((prev) => ({ ...prev, [name]: lang?.required || "Required" }));
      } else if (
        scheduleType === "event" &&
        (name === "organizerName" ||
          name === "startDate" ||
          name === "endDate" ||
          (isLocationRequired && name === "location"))
      ) {
        setErrors((prev) => ({ ...prev, [name]: lang?.required || "Required" }));
      } else if (
        (scheduleType === "webinar" || scheduleType === "clinical_session") &&
        (name === "startDate" || name === "startTime" || name === "duration")
      ) {
        setErrors((prev) => ({ ...prev, [name]: lang?.required || "Required" }));
      }
    } else if (name === "audienceLimit") {
      if (!isValidAudienceLimit(value)) {
        setErrors((prev) => ({
          ...prev,
          audienceLimit: lang?.audience_limit_greater_than_0 || "Audience limit must be greater than 0",
        }));
      }
    }
  };

  const handleProcessBannerFile = (file) => {
    if (!file) return;
    const { imageFiles } = categorizeFiles([file]);
    if (imageFiles.length === 0) {
      showToast(lang?.please_select_valid_image || "Please select a valid image file", "error");
      return;
    }
    readFileAsDataUrl(imageFiles[0])
      .then((dataUrl) => {
        setCropImageSrc(dataUrl);
        setCropModalOpen(true);
      })
      .catch((err) => {
        console.error("Error reading banner file:", err);
      });
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleProcessBannerFile(file);
    }
    e.target.value = "";
  };

  const bannerDropHandlers = createDropzoneHandlers((files) => {
    if (files && files.length > 0) {
      handleProcessBannerFile(files[0]);
    }
  }, setBannerDragActive);

  const handleCropComplete = (croppedUrl) => {
    setBannerPreview(croppedUrl);
    setCropModalOpen(false);
    setErrors((prev) => (prev.banner ? { ...prev, banner: "" } : prev));
  };

  const handleRemoveBanner = () => {
    setBannerPreview(null);
    setCropImageSrc(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    if (requiresBanner) {
      setErrors((prev) => ({ ...prev, banner: lang?.required || "Required" }));
    }
  };

  const handleQuestionDragStart = (e, index) => {
    setDraggedQuestionIndex(index);
    e.dataTransfer.effectAllowed = "move";
    try {
      e.dataTransfer.setData("text/plain", String(index));
    } catch { }
  };

  const handleQuestionDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverQuestionIndex !== index) {
      setDragOverQuestionIndex(index);
    }
  };

  const handleQuestionDrop = (e, targetIndex) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggedQuestionIndex === null || draggedQuestionIndex === targetIndex) {
      setDraggedQuestionIndex(null);
      setDragOverQuestionIndex(null);
      return;
    }

    setQuestions((prev) => {
      const updated = [...prev];
      const [draggedItem] = updated.splice(draggedQuestionIndex, 1);
      updated.splice(targetIndex, 0, draggedItem);
      return updated;
    });

    setDraggedQuestionIndex(null);
    setDragOverQuestionIndex(null);
  };

  const handleQuestionDragEnd = () => {
    setDraggedQuestionIndex(null);
    setDragOverQuestionIndex(null);
  };

  const handleAddQuestion = (questionPayload) => {
    const normalizedQuestion = normalizeAgendaQuestionsForForm([
      questionPayload,
    ])[0];

    if (!normalizedQuestion) return;

    if (editingQuestionIndex !== null) {
      setQuestions((prev) => {
        const next = [...prev];
        next[editingQuestionIndex] = normalizedQuestion;
        return next;
      });
      setEditingQuestionIndex(null);
    } else {
      setQuestions((prev) => [...prev, normalizedQuestion]);
    }
  };

  const handleRemoveQuestion = (idx) => {
    setQuestions((prev) => prev.filter((_, i) => i !== idx));
    if (editingQuestionIndex === idx) setEditingQuestionIndex(null);
    setActiveQuestionMenuIndex(null);
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = lang?.required || "Required";
    }

    if (requiresBanner && !bannerPreview) {
      newErrors.banner = lang?.required || "Required";
    }

    if (scheduleType === "event") {
      if (!formData.organizerName.trim()) newErrors.organizerName = lang?.required || "Required";
      if (!formData.category) newErrors.category = lang?.required || "Required";
      if (!formData.startDate) newErrors.startDate = lang?.required || "Required";
      if (!formData.endDate) newErrors.endDate = lang?.required || "Required";
      if (isLocationRequired) {
        if (!formData.location?.trim()) {
          newErrors.location = lang?.required || "Required";
        } else if (!tryParseLatLng(formData.lat_lng)) {
          newErrors.location =
            lang?.select_location_on_map || "Please select location on map";
        }
      }
      if (!isValidAudienceLimit(formData.audienceLimit)) {
        newErrors.audienceLimit =
          lang?.audience_limit_greater_than_0 ||
          "Audience limit must be a whole number greater than 0";
      }
      if (!formData.description.trim()) newErrors.description = lang?.required || "Required";

      if (formData.startDate && formData.endDate && moment(formData.endDate).isBefore(moment(formData.startDate), "day")) {
        newErrors.endDate = lang?.end_date_cannot_be_before_start_date || "End date cannot be before start date";
      }

      const isStartToday = Boolean(formData.startDate) && moment(formData.startDate).isSame(moment(), "day");

      if (isStartToday) {
        if (!formData.startTime) {
          newErrors.startTime =
            lang?.please_enter_event_start_time || "Please enter event start time.";
        } else if (!editEvent) {
          const startDT = moment(
            `${formData.startDate} ${formData.startTime}`,
            ["YYYY-MM-DD HH:mm", "YYYY-MM-DD hh:mm A"]
          );
          if (startDT.isBefore(moment())) {
            newErrors.startTime =
              lang?.start_time_cannot_be_in_the_past || "Start time cannot be in the past";
          }
        }
      }

      if (formData.startDate && formData.endDate && moment(formData.startDate).isSame(moment(formData.endDate), "day")) {
        if (formData.startTime && formData.endTime) {
          const startDT = moment(
            `${formData.startDate} ${formData.startTime}`,
            ["YYYY-MM-DD HH:mm", "YYYY-MM-DD hh:mm A"]
          );
          const endDT = moment(
            `${formData.endDate} ${formData.endTime}`,
            ["YYYY-MM-DD HH:mm", "YYYY-MM-DD hh:mm A"]
          );
          if (endDT.isSameOrBefore(startDT)) {
            newErrors.endTime =
              lang?.end_time_must_be_after_start_time || "End time must be after start time";
          }
        }
      }
    } else if (scheduleType === "webinar" || scheduleType === "clinical_session") {
      if (!formData.category) newErrors.category = lang?.required || "Required";
      if (!formData.startDate) newErrors.startDate = lang?.required || "Required";
      if (!formData.startTime) newErrors.startTime = lang?.required || "Required";
      if (!formData.duration) newErrors.duration = lang?.required || "Required";
      if (!formData.description.trim()) newErrors.description = lang?.required || "Required";

      const dur = Number(formData.duration);
      if (isNaN(dur) || dur < 15 || dur > 120) {
        newErrors.duration = lang?.duration_must_be_between_15_and_120 || "Duration must be between 15 and 120 minutes";
      }

      if (!editEvent && formData.startDate && formData.startTime) {
        const combinedDateTime = moment(`${formData.startDate} ${formData.startTime}`, "YYYY-MM-DD HH:mm");
        if (combinedDateTime.isBefore(moment())) {
          newErrors.startTime = lang?.start_time_cannot_be_in_the_past || "Start time cannot be in the past";
        } else if (combinedDateTime.diff(moment(), "minutes") < 5) {
          newErrors.startTime = lang?.start_time_at_least_5_mins || "Start time must be at least 5 minutes in the future";
        }
      }
    }

    return newErrors;
  };

  const isFormValid = () => {
    if (!formData.title?.trim()) return false;
    if (requiresBanner && !bannerPreview) return false;
    if (!formData.category) return false;
    if (!formData.startDate) return false;
    if (!formData.description?.trim()) return false;

    if (scheduleType === "event") {
      if (!formData.organizerName?.trim()) return false;
      if (!formData.endDate) return false;
      if (isLocationRequired) {
        if (!formData.location?.trim()) return false;
      }
      if (!isValidAudienceLimit(formData.audienceLimit)) {
        return false;
      }
      if (formData.startDate && formData.endDate && moment(formData.endDate).isBefore(moment(formData.startDate), "day")) {
        return false;
      }
      const isStartToday = Boolean(formData.startDate) && moment(formData.startDate).isSame(moment(), "day");
      if (isStartToday && !formData.startTime) {
        return false;
      }
    } else if (scheduleType === "webinar" || scheduleType === "clinical_session") {
      if (!formData.startTime) return false;
      if (!formData.duration) return false;
      const dur = Number(formData.duration);
      if (isNaN(dur) || dur < 15 || dur > 120) return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isLoadingDetails) return;

    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});

    // Additional validations matching post tabs behavior
    if (scheduleType === "webinar" || scheduleType === "clinical_session") {
      const dur = Number(formData.duration);
      if (dur > 120) {
        showToast(lang?.duration_must_be_between_15_and_120 || "Duration cannot exceed 120 minutes.", "error");
        return;
      }
      if (dur < 15) {
        showToast(lang?.duration_must_be_between_15_and_120 || "Duration must be at least 15 minutes.", "error");
        return;
      }
      if (!editEvent) {
        const formattedStartTime = moment(formData.startTime, ["HH:mm", "hh:mm A"]).format("hh:mm A");
        const combinedDateTime = moment(
          `${formData.startDate} ${formattedStartTime}`,
          "YYYY-MM-DD hh:mm A"
        );
        if (combinedDateTime.isBefore(moment())) {
          showToast(lang?.start_time_cannot_be_in_the_past || "Start time cannot be in the past.", "error");
          return;
        }
        if (combinedDateTime.diff(moment(), "minutes") < 5) {
          showToast(lang?.start_time_at_least_5_mins || "Select at least 5 minutes after the current time.", "error");
          return;
        }
      }
    } else if (scheduleType === "event") {
      if (!isValidAudienceLimit(formData.audienceLimit)) {
        showToast(
          lang?.audience_limit_greater_than_0 ||
          "Audience limit must be a whole number greater than 0.",
          "error"
        );
        return;
      }
      if (moment(formData.endDate).isBefore(moment(formData.startDate), "day")) {
        showToast(lang?.end_date_cannot_be_before_start_date || "End date cannot be before start date.", "error");
        return;
      }
      const isStartToday = Boolean(formData.startDate) && moment(formData.startDate).isSame(moment(), "day");
      if (isStartToday) {
        if (!formData.startTime) {
          showToast(
            lang?.please_enter_event_start_time || "Please enter event start time.",
            "error"
          );
          return;
        }
        if (!editEvent) {
          const startDT = moment(
            `${formData.startDate} ${formData.startTime}`,
            ["YYYY-MM-DD HH:mm", "YYYY-MM-DD hh:mm A"]
          );
          if (startDT.isBefore(moment())) {
            showToast(
              lang?.start_time_cannot_be_in_the_past || "Start time cannot be in the past.",
              "error"
            );
            return;
          }
        }
      }

      if (formData.startDate === formData.endDate && formData.startTime && formData.endTime) {
        const startDT = moment(
          `${formData.startDate} ${formData.startTime}`,
          ["YYYY-MM-DD HH:mm", "YYYY-MM-DD hh:mm A"]
        );
        const endDT = moment(
          `${formData.endDate} ${formData.endTime}`,
          ["YYYY-MM-DD HH:mm", "YYYY-MM-DD hh:mm A"]
        );
        if (endDT.isSameOrBefore(startDT)) {
          showToast(lang?.end_time_must_be_after_start_time || "End time must be after start time.", "error");
          return;
        }
      }
    }

    const selectedSub = splashSubcategories.find(
      (item) =>
        (item.name || item.title) === formData.category ||
        String(item.id) === String(formData.category)
    );
    const subcategoryId =
      (selectedSub && selectedSub.id ? String(selectedSub.id) : null) ||
      (editEvent && (editEvent.subcategory_id || editEvent.raw?.subcategory_id || editEvent.raw?.subcategory?.id)
        ? String(editEvent.subcategory_id || editEvent.raw?.subcategory_id || editEvent.raw?.subcategory?.id)
        : null) ||
      (splashSubcategories[0]?.id ? String(splashSubcategories[0].id) : "1");

    const isStartToday = Boolean(formData.startDate) && moment(formData.startDate).isSame(moment(), "day");

    let formattedStartTime = "";
    if (formData.startTime) {
      formattedStartTime = moment(formData.startTime, ["HH:mm", "hh:mm A", "HH:mm:ss"]).format("hh:mm A");
    } else if (scheduleType === "event" && !isStartToday) {
      formattedStartTime = "12:01 AM";
    } else if (scheduleType === "event") {
      formattedStartTime = moment().add(15, "minutes").format("hh:mm A");
    } else {
      formattedStartTime = moment(formData.startTime, ["HH:mm", "hh:mm A", "HH:mm:ss"]).format("hh:mm A");
    }

    const combinedDateTime = moment(
      `${formData.startDate} ${formattedStartTime}`,
      "YYYY-MM-DD hh:mm A"
    );
    const startTimestamp = Math.floor(combinedDateTime.valueOf() / 1000);

    setIsSubmitting(true);

    try {
      if (scheduleType === "webinar") {
        const webinarDurationMinutes = Number(formData.duration) || 60;
        const webinarEndTimestamp = startTimestamp + webinarDurationMinutes * 60;

        const finalWebinarCover =
          bannerPreview ||
          editEvent?.image ||
          editEvent?.cover ||
          editEvent?.cover_image ||
          editEvent?.raw?.cover ||
          editEvent?.raw?.image ||
          (Array.isArray(editEvent?.media) ? editEvent?.media[0] : editEvent?.media) ||
          (Array.isArray(editEvent?.raw?.media) ? editEvent?.raw?.media[0] : editEvent?.raw?.media) ||
          null;

        if (editEvent) {
          const rawId =
            editEvent.apiId ||
            editEvent.raw?.id ||
            editEvent.id?.toString().replace(/^(webinar-|session-|evt-)/, "");

          const updatePayload = {
            post_id: rawId,
            start_timestamp: startTimestamp,
            end_timestamp: webinarEndTimestamp,
            type: "webinar",
            caption: formData.description || "",
            schedule_date: formData.startDate,
            schedule_time: formattedStartTime,
            category_id: "3",
            subcategory_id: subcategoryId,
            title: formData.title,
            visibility: formData.visibility || "Public",
            duration: formData.duration || "60",
            ...(bannerPreview?.startsWith("data:")
              ? { mediaBase64: [bannerPreview] }
              : bannerPreview === null
                ? { mediaBase64: [] }
                : {}),
          };

          const response = await axios.post(
            EditPostApi,
            updatePayload,
            authorizeMultipart()
          );

          if (response?.data?.status) {
            const updatedEvent = {
              ...editEvent,
              title: formData.title,
              subtitle: formData.category,
              date: formData.startDate,
              endDate: formData.startDate,
              time: `${formattedStartTime} (${formData.duration || 60} mins)`,
              startTime: formData.startTime,
              start_timestamp: startTimestamp,
              end_timestamp: webinarEndTimestamp,
              duration: formData.duration || "60",
              visibility: formData.visibility || "Public",
              recurrence: formData.recurrence || "One Time",
              description: formData.description || "",
              image: finalWebinarCover,
              cover: finalWebinarCover,
              cover_image: finalWebinarCover,
              media: finalWebinarCover ? [finalWebinarCover] : [],
              tags: [formData.category, "Webinar", formData.visibility || "Public"],
              isCreator: true,
            };

            if (onUpdateEvent) onUpdateEvent(updatedEvent);
            else if (onAddEvent) onAddEvent(updatedEvent);

            const inviteItemId = getResponseScheduleId(response, rawId);
            if (inviteItemId && onInviteSchedule) {
              onInviteSchedule({ itemId: inviteItemId, type: "webinar" });
            }

            showToast({
              title: lang?.webinar_updated || "Webinar Updated",
              message:
                (lang?.item_updated_success || `"${formData.title}" updated successfully!`).replace("{name}", formData.title || ""),
              type: "success",
            });
            toggle();
          } else {
            showToast(extractApiErrorMessage(response?.data, "Failed to update webinar"), "error");
          }
        } else {
          const webinarPayload = {
            start_timestamp: startTimestamp,
            end_timestamp: webinarEndTimestamp,
            type: "webinar",
            caption: formData.description || "",
            schedule_date: formData.startDate,
            schedule_time: formattedStartTime,
            category_id: "3",
            subcategory_id: subcategoryId,
            title: formData.title,
            visibility: formData.visibility || "Public",
            duration: formData.duration || "60",
            mediaBase64: bannerPreview?.startsWith("data:")
              ? [bannerPreview]
              : bannerPreview
                ? [bannerPreview]
                : [],
          };

          const response = await axios.post(
            `${adminBaseURL}user/post/create`,
            webinarPayload,
            authorizeMultipart()
          );

          if (response?.data?.status) {
            const createdPost = response.data.post || response.data.data;
            const createdId = getResponseScheduleId(response);
            const newEvent = {
              id: createdId ? `webinar-${createdId}` : `webinar-${Date.now()}`,
              apiId: createdId,
              title: formData.title,
              subtitle: formData.category,
              type: "webinar",
              host: createdPost?.user?.name || "Live Webinar Host",
              hostRole: createdPost?.user?.specialization || "Ophthalmology Specialist",
              date: formData.startDate,
              endDate: formData.startDate,
              time: `${formattedStartTime} (${formData.duration || 60} mins)`,
              startTime: formData.startTime,
              duration: formData.duration || "60",
              location: "Online HD Room",
              shortLocation: "Online Room",
              isOnline: true,
              visibility: formData.visibility || "Public",
              recurrence: formData.recurrence || "One Time",
              description: formData.description || "Interactive medical webinar.",
              price: "Free",
              image: bannerPreview,
              attendeesCount: 0,
              status: "upcoming",
              countdown: "Starts soon",
              coverColor: "linear-gradient(135deg, #4A154B 0%, #854DDE 100%)",
              tags: [formData.category, "Webinar", formData.visibility || "Public"],
              isJoined: false,
              isSaved: false,
              isCreator: true,
              group: "today",
            };

            onAddEvent(newEvent);
            showToast({
              title: lang?.webinar_created || "Webinar Created",
              message:
                (lang?.item_created_success || `"${formData.title}" created successfully!`).replace("{name}", formData.title || ""),
              type: "success",
            });

            if (createdId && onInviteSchedule) {
              onInviteSchedule({ itemId: createdId, type: "webinar" });
            }
            toggle();
          } else {
            showToast(extractApiErrorMessage(response?.data, "Failed to create webinar"), "error");
          }
        }
      } else if (scheduleType === "clinical_session") {
        const sessionDurationMinutes = Number(formData.duration) || 45;
        const sessionEndTimestamp = startTimestamp + sessionDurationMinutes * 60;

        if (editEvent) {
          const rawId =
            editEvent.apiId ||
            editEvent.raw?.id ||
            editEvent.id?.toString().replace(/^(webinar-|session-|evt-)/, "");

          const sessionPayload = {
            post_id: rawId,
            start_timestamp: startTimestamp,
            end_timestamp: sessionEndTimestamp,
            type: "clinical-session",
            caption: formData.description || "",
            schedule_date: formData.startDate,
            schedule_time: formattedStartTime,
            category_id: "3",
            subcategory_id: subcategoryId,
            title: formData.title,
            visibility: formData.recurrence || "One Time",
            duration: formData.duration || "45",
          };

          const response = await axios.post(
            EditPostApi,
            sessionPayload,
            authorizeMultipart()
          );

          if (response?.data?.status) {
            const updatedEvent = {
              ...editEvent,
              title: formData.title,
              subtitle: formData.category,
              date: formData.startDate,
              endDate: formData.startDate,
              time: `${formattedStartTime} (${formData.duration || 45} mins)`,
              startTime: formData.startTime,
              start_timestamp: startTimestamp,
              end_timestamp: sessionEndTimestamp,
              duration: formData.duration || "45",
              visibility: formData.recurrence || "One Time",
              recurrence: formData.recurrence || "One Time",
              description: formData.description || "",
              tags: [formData.category, "Clinical Session", formData.recurrence || "One Time"],
              isCreator: true,
            };

            if (onUpdateEvent) onUpdateEvent(updatedEvent);
            else if (onAddEvent) onAddEvent(updatedEvent);

            const inviteItemId = getResponseScheduleId(response, rawId);
            if (inviteItemId && onInviteSchedule) {
              onInviteSchedule({ itemId: inviteItemId, type: "clinical_session" });
            }

            showToast({
              title: lang?.clinical_session_updated || "Clinical Session Updated",
              message:
                (lang?.item_updated_success || `"${formData.title}" updated successfully!`).replace("{name}", formData.title || ""),
              type: "success",
            });
            toggle();
          } else {
            showToast(extractApiErrorMessage(response?.data, "Failed to update clinical session"), "error");
          }
        } else {
          const sessionPayload = {
            start_timestamp: startTimestamp,
            end_timestamp: sessionEndTimestamp,
            type: "clinical-session",
            caption: formData.description || "",
            schedule_date: formData.startDate,
            schedule_time: formattedStartTime,
            category_id: "3",
            subcategory_id: subcategoryId,
            title: formData.title,
            visibility: formData.recurrence || "One Time",
            duration: formData.duration || "45",
          };

          const response = await axios.post(
            `${adminBaseURL}user/post/create`,
            sessionPayload,
            authorizeMultipart()
          );

          if (response?.data?.status) {
            const createdPost = response.data.post || response.data.data;
            const createdId = getResponseScheduleId(response);
            const newEvent = {
              id: createdId ? `session-${createdId}` : `session-${Date.now()}`,
              apiId: createdId,
              title: formData.title,
              subtitle: formData.category,
              type: "clinical_session",
              host: createdPost?.user?.name || "Clinical Grand Rounds Host",
              hostRole: createdPost?.user?.specialization || "Department Specialist",
              date: formData.startDate,
              endDate: formData.startDate,
              time: `${formattedStartTime} (${formData.duration || 45} mins)`,
              startTime: formData.startTime,
              duration: formData.duration || "45",
              location: "Case Discussion Room",
              shortLocation: "Online Session",
              isOnline: true,
              visibility: formData.recurrence || "One Time",
              recurrence: formData.recurrence || "One Time",
              description: formData.description || "Case discussion agenda and clinical grand rounds review.",
              price: "Free",
              image: null,
              attendeesCount: 0,
              status: "upcoming",
              countdown: "Starts soon",
              coverColor: "linear-gradient(135deg, #134e4a 0%, #2a9d8f 100%)",
              tags: [formData.category, "Clinical Session", formData.recurrence || "One Time"],
              isJoined: false,
              isSaved: false,
              isCreator: true,
              group: "today",
            };

            onAddEvent(newEvent);
            showToast({
              title: lang?.clinical_session_created || "Clinical Session Created",
              message:
                (lang?.item_created_success || `"${formData.title}" created successfully!`).replace("{name}", formData.title || ""),
              type: "success",
            });

            if (createdId && onInviteSchedule) {
              onInviteSchedule({ itemId: createdId, type: "clinical_session" });
            }
            toggle();
          } else {
            showToast(extractApiErrorMessage(response?.data, "Failed to create clinical session"), "error");
          }
        }
      } else {
        // Event creation / update
        const formattedEndTime = formData.endTime
          ? moment(formData.endTime, ["HH:mm", "hh:mm A", "HH:mm:ss"]).format("hh:mm A")
          : "11:59 PM";
        const isEventOnline =
          formData.availability === "Online" ||
          formData.availability === "Online & Offline" ||
          formData.location?.toLowerCase().includes("online");

        if (editEvent) {
          const rawId =
            editEvent.apiId ||
            editEvent.raw?.id ||
            editEvent.id?.toString().replace(/^(evt-|event-)/, "");

          const finalEventCover =
            bannerPreview ||
            editEvent?.image ||
            editEvent?.cover ||
            editEvent?.cover_image ||
            editEvent?.raw?.cover ||
            editEvent?.raw?.image ||
            null;

          const rawPriceStr = String(formData.price || "").trim();
          const parsedIntPrice = parseInt(rawPriceStr, 10);
          const isFreePrice =
            !rawPriceStr ||
            isNaN(parsedIntPrice) ||
            parsedIntPrice <= 0 ||
            rawPriceStr.toLowerCase() === "free" ||
            rawPriceStr === "@empty_data_";
          const sanitizedPrice = isFreePrice ? "" : String(parsedIntPrice);

          const eventEndDate = formData.endDate || formData.startDate;
          const eventEndTimestamp = Math.floor(
            moment(
              `${eventEndDate} ${formattedEndTime}`,
              "YYYY-MM-DD hh:mm A"
            ).valueOf() / 1000
          );

          const editPayload = {
            event_id: rawId,
            start_timestamp: startTimestamp,
            end_timestamp: eventEndTimestamp,
            title: formData.title,
            date: formData.startDate,
            start_date: formData.startDate,
            end_date: eventEndDate,
            time: formattedStartTime,
            start_time: formattedStartTime,
            end_time: formattedEndTime,
            location: formData.location || "",
            short_location:
              getShortLocation(formData.location, locationData, isEventOnline) ||
              (isEventOnline ? "Online" : ""),
            address: formData.location || "",
            lat_lng: formData.lat_lng
              ? (typeof formData.lat_lng === "string"
                ? formData.lat_lng
                : `${formData.lat_lng.lat},${formData.lat_lng.lng}`)
              : "@empty_data_",
            description: formData.description,
            availability: formData.availability || "Online",
            visibility: formData.visibility || "Public",
            limit: formData.audienceLimit ? parseInt(formData.audienceLimit, 10) : "@empty_data_",
            price: sanitizedPrice ? sanitizedPrice : "@empty_data_",
            website: formData.website || "@empty_data_",
            link: formData.registrationLink || "@empty_data_",
            password: formData.password || "@empty_data_",
            tags: "@medical",
            category_id: "3",
            subcategory_id: subcategoryId,
            ...(bannerPreview?.startsWith("data:")
              ? { cover_base64: bannerPreview }
              : bannerPreview === null
                ? { cover_base64: "@empty_data_" }
                : {}),
          };

          const response = await axios.post(
            EditEventApi,
            editPayload,
            authorizeMultipart()
          );

          if (response?.data?.status) {
            let questionSyncError = null;
            try {
              await syncAgendaEventQuestions({
                eventId: rawId,
                initialQuestions,
                questions,
              });
            } catch (qErr) {
              console.error("Error syncing event questions:", qErr);
              questionSyncError = qErr;
            }

            const updatedEvent = {
              ...editEvent,
              title: formData.title,
              subtitle: formData.category,
              host: formData.organizerName || editEvent.host || "Organizer",
              date: formData.startDate,
              startDate: formData.startDate,
              endDate: eventEndDate,
              time: `${formattedStartTime} - ${formattedEndTime}`,
              startTime: formData.startTime,
              endTime: formData.endTime,
              start_timestamp: startTimestamp,
              end_timestamp: eventEndTimestamp,
              location: formData.location || (isEventOnline ? "Online HD Room" : ""),
              shortLocation:
                getShortLocation(formData.location, locationData, isEventOnline) ||
                (isEventOnline ? "Online" : ""),
              short_location:
                getShortLocation(formData.location, locationData, isEventOnline) ||
                (isEventOnline ? "Online" : ""),
              address: formData.location || "",
              lat_lng: formData.lat_lng || null,
              isOnline: isEventOnline,
              description: formData.description,
              price: sanitizedPrice && Number(sanitizedPrice) > 0 ? `${formData.currency} ${sanitizedPrice}` : "Free",
              priceValue: sanitizedPrice && Number(sanitizedPrice) > 0 ? sanitizedPrice : "",
              image: finalEventCover,
              cover: finalEventCover,
              cover_image: finalEventCover,
              maxAttendees: formData.audienceLimit
                ? parseInt(formData.audienceLimit, 10)
                : null,
              tags: [formData.category, "Event", formData.availability],
              questions: questions,
              website: formData.website,
              registrationLink: formData.registrationLink,
              availability: formData.availability,
              recurrence: formData.recurrence || "One Time",
              password: formData.password,
              isCreator: true,
            };

            if (onUpdateEvent) onUpdateEvent(updatedEvent);
            else if (onAddEvent) onAddEvent(updatedEvent);

            showToast({
              title: lang?.event_updated || "Event Updated",
              message: questionSyncError
                ? (lang?.event_questions_sync_error || "Event updated, but one or more questions could not be saved.")
                : (lang?.item_updated_success || `"${formData.title}" updated successfully!`).replace("{name}", formData.title || ""),
              type: questionSyncError ? "error" : "success",
            });
            toggle();
          } else {
            showToast(extractApiErrorMessage(response?.data, "Failed to update event"), "error");
          }
        } else {
          const eventEndDate = formData.endDate || formData.startDate;
          const eventEndTimestamp = Math.floor(
            moment(
              `${eventEndDate} ${formattedEndTime}`,
              "YYYY-MM-DD hh:mm A"
            ).valueOf() / 1000
          );
          const signedInUser = JSON.parse(
            localStorage.getItem("SignupData") || "{}"
          );
          const eventUserId = signedInUser?.data?.id || SplashData?.user?.id;
          const eventLocation =
            formData.location ||
            (isEventOnline ? "Online" : "");
          const eventShortLocation =
            getShortLocation(formData.location, locationData, isEventOnline) ||
            (isEventOnline ? "Online" : "");
          const eventLatLng = formData.lat_lng
            ? typeof formData.lat_lng === "string"
              ? formData.lat_lng
              : `${formData.lat_lng.lat},${formData.lat_lng.lng}`
            : "";
          const rawPriceStr = String(formData.price || "").trim();
          const parsedIntPrice = parseInt(rawPriceStr, 10);
          const isFreePrice =
            !rawPriceStr ||
            isNaN(parsedIntPrice) ||
            parsedIntPrice <= 0 ||
            rawPriceStr.toLowerCase() === "free" ||
            rawPriceStr === "@empty_data_";
          const sanitizedPrice = isFreePrice ? "" : String(parsedIntPrice);

          const eventPayload = {
            cover_base64: bannerPreview?.startsWith("data:") ? bannerPreview : "",
            start_timestamp: startTimestamp,
            end_timestamp: eventEndTimestamp,
            ...(eventUserId ? { user_id: eventUserId } : {}),
            title: formData.title,
            organizer: formData.organizerName || "Organizer",
            description: formData.description,
            date: formData.startDate,
            time: formattedStartTime,
            availability: formData.availability || "Online",
            location: eventLocation,
            short_location: eventShortLocation,
            lat_lng: eventLatLng,
            website: formData.website || "",
            link: formData.registrationLink || "",
            end_date: eventEndDate,
            end_time: formattedEndTime,
            password: formData.password || "",
            limit: formData.audienceLimit
              ? parseInt(formData.audienceLimit, 10)
              : null,
            price: sanitizedPrice,
            tags: "@medical",
            category_id: "3",
            subcategory_id: subcategoryId,
            // Retain the newer aliases for installations that accept them.
            start_date: formData.startDate,
            start_time: formattedStartTime,
            visibility: formData.visibility || "Public",
          };

          const response = await axios.post(
            AddEventApi,
            eventPayload,
            authorizeMultipart()
          );

          if (response?.data?.status) {
            const eventId = getResponseScheduleId(response);

            let questionCreationError = null;
            try {
              await createAgendaEventQuestions(eventId, questions);
            } catch (qErr) {
              console.error("Error creating event questions:", qErr);
              questionCreationError = qErr;
            }

            const timeDisplay = `${formData.startTime} - ${formData.endTime}`;

            const newEvent = {
              id: eventId ? `evt-${eventId}` : `evt-${Date.now()}`,
              apiId: eventId,
              title: formData.title,
              subtitle: formData.category,
              type: "event",
              host: formData.organizerName || "Event Organizer",
              hostRole: "Organizer",
              date: formData.startDate,
              endDate: formData.endDate || formData.startDate,
              time: timeDisplay,
              startTime: formData.startTime,
              endTime: formData.endTime,
              location: formData.location || (isEventOnline ? "Online HD Room" : ""),
              shortLocation: eventShortLocation,
              short_location: eventShortLocation,
              address: formData.location || "",
              lat_lng: formData.lat_lng || null,
              isOnline: isEventOnline,
              description: formData.description,
              price: sanitizedPrice && Number(sanitizedPrice) > 0 ? `${formData.currency} ${sanitizedPrice}` : "Free",
              priceValue: sanitizedPrice && Number(sanitizedPrice) > 0 ? sanitizedPrice : "",
              image: bannerPreview,
              attendeesCount: 0,
              maxAttendees: formData.audienceLimit
                ? parseInt(formData.audienceLimit, 10)
                : null,
              status: "upcoming",
              countdown: "Starts soon",
              coverColor: "linear-gradient(135deg, #061223 0%, #0474DF 100%)",
              tags: [formData.category, "Event", formData.availability],
              isJoined: false,
              isSaved: false,
              isCreator: true,
              group: "today",
              questions: questions,
              website: formData.website,
              registrationLink: formData.registrationLink,
              availability: formData.availability,
              recurrence: formData.recurrence || "One Time",
              password: formData.password,
            };

            onAddEvent(newEvent);
            showToast({
              title: lang?.event_created || "Event Created",
              message: questionCreationError
                ? (lang?.event_questions_create_error || "Event created, but its questions could not be saved.")
                : (lang?.item_created_success || `"${formData.title}" created successfully!`).replace("{name}", formData.title || ""),
              type: questionCreationError ? "error" : "success",
            });
            toggle();
          } else {
            showToast(extractApiErrorMessage(response?.data, "Failed to create event"), "error");
          }
        }
      }
    } catch (apiError) {
      console.error("Error submitting agenda item:", apiError);
      showToast(
        extractApiErrorMessage(apiError, "Failed to submit request"),
        "error"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const getFormTitle = () => {
    if (editEvent) {
      switch (scheduleType) {
        case "webinar":
          return lang?.edit_webinar || "Edit Webinar";
        case "clinical_session":
          return lang?.edit_clinical_session || lang?.edit_session || "Edit Clinical Session";
        case "event":
        default:
          return lang?.edit_event || "Edit Event";
      }
    }
    switch (scheduleType) {
      case "webinar":
        return lang?.create_webinar || "Create Webinar";
      case "clinical_session":
        return lang?.create_clinical_session || "Create Clinical Session";
      case "event":
      default:
        return lang?.create_event || "Create Event";
    }
  };

  return (
    <>
      {/* Dark Blur Backdrop */}
      <div
        className={`agenda-drawer-backdrop ${isOpen && slideIn ? "open" : isClosing ? "closing" : ""}`}
        onClick={handleBackdropClick}
      />

      {/* Slide-in Drawer Panel */}
      <div className={`agenda-drawer-panel ${isOpen && slideIn ? "open" : isClosing ? "closing" : ""} ${shake ? "shake" : ""}`}>
        {/* Drawer Header Bar */}
        <div className="agenda-drawer-header-bar">
          <div className="d-flex flex-column gap-1">
            <h4 className="agenda-drawer-header-title">
              {isLoadingDetails
                ? (lang?.loading_event || "Loading Event")
                : editEvent
                  ? getFormTitle()
                  : (lang?.create_new || "Create New")}
            </h4>
            <p className="agenda-drawer-header-subtitle">
              {isLoadingDetails
                ? (lang?.fetching_event_details || "Fetching the complete event details...")
                : editEvent
                  ? (scheduleType === "webinar"
                    ? (lang?.update_details_of_webinar || "Update the details of your webinar")
                    : scheduleType === "clinical_session"
                      ? (lang?.update_details_of_clinical_session || "Update the details of your clinical session")
                      : (lang?.update_details_of_event || "Update the details of your event"))
                  : (lang?.select_what_to_create || "Select what you want to create")}
            </p>
          </div>

          <button
            type="button"
            className="agenda-drawer-close-btn"
            onClick={toggle}
            title={lang?.close || "Close Drawer"}
            aria-label={lang?.close || "Close Drawer"}
          >
            <LuX size={18} />
          </button>
        </div>

        {/* Drawer Form Wrapper */}
        <form onSubmit={handleSubmit} className="agenda-drawer-form">
          {/* Scrollable Drawer Body Content */}
          <div
            className="agenda-drawer-body"
            aria-busy={isLoadingDetails}
          >
            {isLoadingDetails && (
              <div
                className="agenda-drawer-details-loader"
                role="status"
                aria-live="polite"
              >
                <LuLoader2 className="agenda-spin-anim" size={28} />
                <span>{lang?.loading_event || "Loading event details..."}</span>
              </div>
            )}
            {/* Schedule Type Selector Cards & Mentorship Notice */}
            {!editEvent && !hideTopCards && (
              <>
                <div
                  className="agenda-drawer-types-grid"
                  style={
                    allowedTypes.length < 3
                      ? { gridTemplateColumns: `repeat(${allowedTypes.length}, 1fr)` }
                      : {}
                  }
                >
                  {/* 1. Event Card */}
                  {allowedTypes.includes("event") && (
                    <div
                      className={`agenda-drawer-type-card ${scheduleType === "event" ? "active-event" : ""}`}
                      onClick={() => {
                        if (editEvent || scheduleType === "event") return;
                        resetForm("event");
                      }}
                      style={editEvent && scheduleType !== "event" ? { opacity: 0.45, cursor: "not-allowed" } : {}}
                    >
                      <div
                        style={{
                          color: "#44A30B",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <LuCalendarPlus size={28} />
                      </div>
                      <span className="agenda-drawer-type-title">{lang?.event || "Event"}</span>
                      <span className="agenda-drawer-type-desc">
                        {lang?.event_card_desc || "Conferences, seminars, workshops"}
                      </span>
                    </div>
                  )}

                  {/* 2. Webinar Card */}
                  {allowedTypes.includes("webinar") && (
                    <div
                      className={`agenda-drawer-type-card ${scheduleType === "webinar" ? "active-webinar" : ""}`}
                      onClick={() => {
                        if (editEvent || scheduleType === "webinar") return;
                        resetForm("webinar");
                      }}
                      style={editEvent && scheduleType !== "webinar" ? { opacity: 0.45, cursor: "not-allowed" } : {}}
                    >
                      <div
                        style={{
                          color: "#854DDE",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <LuVideo size={28} />
                      </div>
                      <span className="agenda-drawer-type-title">{lang?.webinar || "Webinar"}</span>
                      <span className="agenda-drawer-type-desc">
                        {lang?.webinar_card_desc || "Live streams, discussions, Q&A"}
                      </span>
                    </div>
                  )}

                  {/* 3. Clinical Session Card */}
                  {allowedTypes.includes("clinical_session") && (
                    <div
                      className={`agenda-drawer-type-card ${scheduleType === "clinical_session" ? "active-clinical_session" : ""}`}
                      onClick={() => {
                        if (editEvent || scheduleType === "clinical_session") return;
                        resetForm("clinical_session");
                      }}
                      style={editEvent && scheduleType !== "clinical_session" ? { opacity: 0.45, cursor: "not-allowed" } : {}}
                    >
                      <div
                        style={{
                          color: "#0474DF",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <LuStethoscope size={28} />
                      </div>
                      <span className="agenda-drawer-type-title">{lang?.clinical_session || "Clinical Session"}</span>
                      <span className="agenda-drawer-type-desc">
                        {lang?.clinical_session_card_desc || "Interactive clinical discussions"}
                      </span>
                    </div>
                  )}
                </div>

                {/* Mentorship Notice Banner */}
                {!hideMentorshipNotice && allowedTypes.includes("event") && (
                  <div className="agenda-drawer-notice-banner">
                    <LuBookmark size={18} color="#ED7208" style={{ flexShrink: 0 }} />
                    <span className="agenda-drawer-notice-text">
                      {lang?.mentorship_notice ||
                        "Mentorships can’t be created here they appear once a mentor confirms a slot."}
                    </span>
                  </div>
                )}
              </>
            )}

            {/* Form Section Header (Only for new creation mode with top cards) */}
            {!editEvent && !hideTopCards && (
              <div className="d-flex align-items-center justify-content-between pt-1">
                <h5 className="agenda-drawer-section-title">{getFormTitle()}</h5>
              </div>
            )}

            {/* Banner Upload Box (Shown for Event & Webinar, hidden for Clinical Session) */}
            {requiresBanner && (
              <div className="agenda-drawer-upload-field">
                <div
                  className={`agenda-drawer-upload-box ${errors.banner ? "has-error" : ""} ${bannerDragActive ? "drag-active" : ""}`}
                  onClick={() => {
                    if (!bannerPreview) {
                      fileInputRef.current?.click();
                    }
                  }}
                  {...bannerDropHandlers}
                >
                  {bannerPreview ? (
                    <div
                      className="agenda-drawer-upload-preview-wrap"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsPreviewOpen(true);
                      }}
                      style={{ cursor: "pointer" }}
                      title={lang?.click_to_preview_full_image || "Click to preview full image"}
                    >
                      <img
                        src={resolveAgendaImageUrl(bannerPreview, bannerPreview)}
                        alt="Banner Preview"
                        className="agenda-drawer-upload-img"
                      />
                      <div className="agenda-drawer-upload-zoom-badge" title={lang?.click_to_preview_full_image || "Preview image"}>
                        <LuMaximize2 size={16} />
                      </div>
                      <button
                        type="button"
                        className="agenda-drawer-upload-remove-btn"
                        title={lang?.remove_image || "Remove Image"}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveBanner();
                        }}
                      >
                        <LuX size={15} />
                      </button>
                    </div>
                  ) : (
                    <>
                      <TbPhotoUp size={38} className="agenda-drawer-upload-icon" />
                      <span className="agenda-drawer-upload-title">
                        {scheduleType === "webinar"
                          ? (lang?.upload_webinar_banner || "Upload Webinar Banner / Cover Photo")
                          : (lang?.add_event_banner || "Add Event Banner")}
                        <span className="agenda-drawer-req-star">*</span>
                      </span>
                      <span className="agenda-drawer-upload-desc">
                        {lang?.drag_and_drop_image_browse || "Drag and drop your image here, or browse"}
                      </span>
                    </>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={handleFileChange}
                  />
                </div>
                {errors.banner && (
                  <span className="agenda-drawer-error-text" role="alert">
                    {errors.banner}
                  </span>
                )}
              </div>
            )}

            {/* =========================================================================
                FIELDS: EVENT
                ========================================================================= */}
            {scheduleType === "event" && (
              <>
                {/* Row 1: Event Title & Organizer Name */}
                <div className="agenda-drawer-form-row">
                  <div className="agenda-drawer-form-group">
                    <label className="agenda-drawer-label">
                      {lang?.event_title || "Event Title"} <span className="agenda-drawer-req-star">*</span>
                    </label>
                    <div className={`agenda-drawer-input-box ${errors.title ? "has-error" : ""}`}>
                      <input
                        type="text"
                        name="title"
                        className="agenda-drawer-input"
                        placeholder={lang?.enter_event_title || "Enter Event Title"}
                        value={formData.title}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </div>
                  </div>

                  <div className="agenda-drawer-form-group">
                    <label className="agenda-drawer-label">
                      {lang?.organizer_name || "Organizer Name"} <span className="agenda-drawer-req-star">*</span>
                    </label>
                    <div className={`agenda-drawer-input-box ${errors.organizerName ? "has-error" : ""}`}>
                      <input
                        type="text"
                        name="organizerName"
                        className="agenda-drawer-input"
                        placeholder={lang?.enter_organizer_name || "Enter Organizer Name"}
                        value={formData.organizerName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </div>
                  </div>
                </div>

                {/* Row 2: Event Category Dropdown */}
                <div className="agenda-drawer-form-group" ref={categoryRef}>
                  <label className="agenda-drawer-label">
                    {lang?.category || "Category"} <span className="agenda-drawer-req-star">*</span>
                  </label>
                  <div
                    className={`agenda-drawer-input-box ${errors.category ? "has-error" : ""}`}
                    style={{ cursor: "pointer", justifyContent: "space-between" }}
                    onClick={() => setCategoryOpen(!categoryOpen)}
                  >
                    <span
                      style={{
                        fontSize: "14px",
                        color: formData.category ? "#031316" : "#8F98A3",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {formData.category || (lang?.select_category || "Select Category")}
                    </span>
                    <LuChevronDown size={16} color="#031316" />
                  </div>

                  {categoryOpen && (
                    <div className="agenda-drawer-dropdown-menu">
                      {categoryOptions.map((cat) => (
                        <div
                          key={cat}
                          className={`agenda-drawer-dropdown-item ${formData.category === cat ? "selected" : ""}`}
                          onClick={() => {
                            setFormData((p) => ({ ...p, category: cat }));
                            setCategoryOpen(false);
                            if (errors.category) setErrors((p) => ({ ...p, category: "" }));
                          }}
                        >
                          <span>{cat}</span>
                          {formData.category === cat && <LuCheck size={14} color="#00BADA" />}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Row 3: Start Date & End Date */}
                <div className="agenda-drawer-form-row">
                  <div className="agenda-drawer-form-group">
                    <label className="agenda-drawer-label">
                      {lang?.start_date || "Start Date"} <span className="agenda-drawer-req-star">*</span>
                    </label>
                    <ModernDatePicker
                      value={formData.startDate}
                      minDate={moment().startOf("day")}
                      onChange={(val) => handleDateChange(val, "startDate")}
                      error={Boolean(errors.startDate)}
                      placeholder="YYYY-MM-DD"
                    />
                  </div>

                  <div className="agenda-drawer-form-group">
                    <label className="agenda-drawer-label">
                      {lang?.end_date || "End Date"} <span className="agenda-drawer-req-star">*</span>
                    </label>
                    <ModernDatePicker
                      value={formData.endDate}
                      minDate={formData.startDate ? moment(formData.startDate).startOf("day") : moment().startOf("day")}
                      disabled={!formData.startDate}
                      onDisabledClick={() => handleDisabledFieldClick("end_date")}
                      onChange={(val) => handleDateChange(val, "endDate")}
                      error={Boolean(errors.endDate)}
                      placeholder="YYYY-MM-DD"
                    />
                  </div>
                </div>

                {/* Row 4: Start Time & End Time */}
                <div className="agenda-drawer-form-row">
                  <div className="agenda-drawer-form-group">
                    <label className="agenda-drawer-label">
                      {lang?.start_time || "Start Time"}
                      {isStartToday && <span className="agenda-drawer-req-star">*</span>}
                    </label>
                    <ModernTimePicker
                      value={formData.startTime}
                      minTime={minStartTime}
                      disabled={!formData.startDate}
                      onDisabledClick={() => handleDisabledFieldClick("start_time")}
                      onChange={(val) => handleTimeChange(val, "startTime")}
                      error={Boolean(errors.startTime)}
                      placeholder="hh:mm A"
                      relatedDate={formData.startDate}
                      relatedEndTime={isSameDay ? formData.endTime : null}
                    />
                  </div>

                  <div className="agenda-drawer-form-group">
                    <label className="agenda-drawer-label">{lang?.end_time || "End Time"}</label>
                    <ModernTimePicker
                      value={formData.endTime}
                      minTime={minEndTime}
                      disabled={!formData.endDate}
                      onDisabledClick={() => handleDisabledFieldClick("end_time")}
                      onChange={(val) => handleTimeChange(val, "endTime")}
                      error={Boolean(errors.endTime)}
                      placeholder="hh:mm A"
                      relatedDate={formData.endDate}
                      relatedStartTime={isSameDay ? formData.startTime : null}
                    />
                  </div>
                </div>

                {/* Row 5: Location (Map extraction first, then editable) */}
                <div className="agenda-drawer-form-group">
                  <div className="d-flex align-items-center justify-content-between mb-1">
                    <label className="agenda-drawer-label" style={{ marginBottom: 0 }}>
                      {lang?.location || "Location"}{" "}
                      {isLocationRequired && <span className="agenda-drawer-req-star">*</span>}
                    </label>
                    <button
                      type="button"
                      className="agenda-drawer-loc-action-btn"
                      onClick={handleLocationClick}
                      disabled={isLocationLoading}
                      title={lang?.select_location_on_map || "Select location on map"}
                    >
                      {isLocationLoading ? (
                        <>
                          <LuLoader2 size={13} color="#00BADA" className="agenda-spin-anim" />
                          <span>{lang?.detecting_location || "Detecting..."}</span>
                        </>
                      ) : (
                        <>
                          <LuMapPin size={13} color="#00BADA" />
                          <span>{lang?.select_location_on_map || "Pick on Map"}</span>
                        </>
                      )}
                    </button>
                  </div>

                  <div className="agenda-drawer-location-textarea-wrap" style={{ position: "relative" }}>
                    <textarea
                      name="location"
                      rows={3}
                      readOnly={!hasLocationCoords}
                      onClick={!hasLocationCoords ? handleLocationClick : undefined}
                      className={`agenda-drawer-textarea agenda-drawer-location-textarea ${errors.location ? "has-error" : ""} ${!hasLocationCoords ? "location-locked" : ""}`}
                      placeholder={
                        !hasLocationCoords
                          ? (lang?.click_to_select_location_on_map || "Click to select location on map")
                          : (lang?.enter_location || "Enter full address / location or select from map...")
                      }
                      value={formData.location}
                      onChange={
                        hasLocationCoords
                          ? (e) => {
                              const val = e.target.value;
                              if (!val || !val.trim()) {
                                setFormData((prev) => ({
                                  ...prev,
                                  location: "",
                                  address: "",
                                  city: "",
                                  country: "",
                                  short_location: "",
                                  lat_lng: null,
                                }));
                                setLocationData(null);
                                setUserCoords(null);
                              } else {
                                setFormData((prev) => ({
                                  ...prev,
                                  location: val,
                                  address: val,
                                }));
                              }
                              if (errors.location) {
                                setErrors((prev) => ({ ...prev, location: "" }));
                              }
                            }
                          : undefined
                      }
                      onBlur={handleBlur}
                      style={{
                        paddingRight: formData.location ? "34px" : "14px",
                      }}
                    />
                    {formData.location && (
                      <button
                        type="button"
                        className="agenda-drawer-loc-clear-btn"
                        title={lang?.clear_location || "Clear Location"}
                        onClick={handleClearLocation}
                        style={{
                          position: "absolute",
                          top: "10px",
                          right: "10px",
                          width: "22px",
                          height: "22px",
                          border: "none",
                          background: "#F1F5F9",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#64748B",
                          cursor: "pointer",
                          transition: "all 0.15s ease",
                        }}
                      >
                        <LuX size={13} />
                      </button>
                    )}
                  </div>
                  {errors.location && (
                    <span className="agenda-drawer-error-text">
                      {errors.location}
                    </span>
                  )}
                </div>

                {/* Row 6: Website & Registration Link */}
                <div className="agenda-drawer-form-row">
                  <div className="agenda-drawer-form-group">
                    <label className="agenda-drawer-label">{lang?.website || "Website"}</label>
                    <div className="agenda-drawer-input-box">
                      <input
                        type="url"
                        name="website"
                        className="agenda-drawer-input"
                        placeholder="https://example.com"
                        value={formData.website}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  <div className="agenda-drawer-form-group">
                    <label className="agenda-drawer-label">{lang?.registration_link || "Registration Link"}</label>
                    <div className="agenda-drawer-input-box">
                      <input
                        type="url"
                        name="registrationLink"
                        className="agenda-drawer-input"
                        placeholder="https://register.com"
                        value={formData.registrationLink}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                </div>

                {/* Row 7: Availability & Audience Limit */}
                <div className="agenda-drawer-form-row">
                  <div className="agenda-drawer-form-group" ref={availabilityRef}>
                    <label className="agenda-drawer-label">{lang?.availability || "Availability"}</label>
                    <div
                      className="agenda-drawer-input-box"
                      style={{ cursor: "pointer", justifyContent: "space-between" }}
                      onClick={() => setAvailabilityOpen(!availabilityOpen)}
                    >
                      <span style={{ fontSize: "14px", color: "#031316" }}>
                        {formData.availability === "Online"
                          ? (lang?.online || "Online")
                          : (formData.availability === "Offline" || formData.availability === "In-Person")
                            ? (lang?.in_person || "In-Person")
                            : (lang?.hybrid || "Hybrid")}
                      </span>
                      <LuChevronDown size={16} color="#031316" />
                    </div>

                    {availabilityOpen && (
                      <div className="agenda-drawer-dropdown-menu">
                        {AVAILABILITY_OPTIONS.map((opt) => {
                          const isSelected =
                            formData.availability === opt ||
                            (opt === "Offline" && formData.availability === "In-Person") ||
                            (opt === "Online & Offline" && formData.availability === "Hybrid");
                          const displayLabel =
                            opt === "Online"
                              ? (lang?.online || "Online")
                              : opt === "Offline"
                                ? (lang?.in_person || "In-Person")
                                : (lang?.hybrid || "Hybrid");

                          return (
                            <div
                              key={opt}
                              className={`agenda-drawer-dropdown-item ${isSelected ? "selected" : ""}`}
                              onClick={() => {
                                setFormData((p) => ({ ...p, availability: opt }));
                                setAvailabilityOpen(false);
                                if (opt === "Online") {
                                  setErrors((p) => {
                                    const next = { ...p };
                                    delete next.location;
                                    delete next.address;
                                    return next;
                                  });
                                }
                              }}
                            >
                              <span>{displayLabel}</span>
                              {isSelected && <LuCheck size={14} color="#00BADA" />}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div className="agenda-drawer-form-group">
                    <label className="agenda-drawer-label">
                      {lang?.audience_limit || "Audience Limit"}{" "}

                    </label>
                    <div className={`agenda-drawer-input-box ${errors.audienceLimit ? "has-error" : ""}`}>
                      <input
                        type="number"
                        name="audienceLimit"
                        min="1"
                        step="1"
                        className="agenda-drawer-input"
                        placeholder="e.g. 100"
                        value={formData.audienceLimit}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </div>
                  </div>
                </div>

                {/* Row 8: Set Password & Event Price */}
                <div className="agenda-drawer-form-row">
                  <div className="agenda-drawer-form-group">
                    <label className="agenda-drawer-label">{lang?.set_password || "Set Password"}</label>
                    <div className="agenda-drawer-input-box">
                      <input
                        type={showPassword ? "text" : "password"}
                        name="agendaPassword"
                        id="agenda-event-password"
                        className="agenda-drawer-input"
                        placeholder={"***********"}
                        value={formData.password}
                        onChange={handleChange}
                        autoComplete="new-password"
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck={false}
                        data-lpignore="true"
                        data-1p-ignore="true"
                        data-bwautofill="false"
                        data-form-type="other"
                        aria-autocomplete="none"
                      />
                      <button
                        type="button"
                        style={{ background: "transparent", border: "none", padding: 0, cursor: "pointer" }}
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <LuEyeOff size={16} color="#031316" /> : <LuEye size={16} color="#031316" />}
                      </button>
                    </div>
                  </div>

                  <div className="agenda-drawer-form-group">
                    <label className="agenda-drawer-label">{lang?.event_price || "Event Price"}</label>
                    <div className="agenda-drawer-input-box">
                      <input
                        type="number"
                        name="price"
                        min="0"
                        step="1"
                        className="agenda-drawer-input"
                        placeholder={lang?.free_or_zero || "0 (Free)"}
                        value={formData.price}
                        onChange={handleChange}
                        onKeyDown={(e) => {
                          if (["e", "E", "+", "-", "."].includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                      />
                      <span className="agenda-drawer-badge-suffix">
                        {formData.currency}
                      </span>
                    </div>
                  </div>

                </div>

                {/* Row 9: Event Description */}
                <div className="agenda-drawer-form-group">
                  <label className="agenda-drawer-label">
                    {lang?.description || "Description"} <span className="agenda-drawer-req-star">*</span>
                  </label>
                  <textarea
                    name="description"
                    className={`agenda-drawer-textarea ${errors.description ? "has-error" : ""}`}
                    placeholder={lang?.event_desc_placeholder || "Write a brief description about the event..."}
                    value={formData.description}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </div>

                {/* Row 10: Create Form / Questions (Figma Group 1171277783) */}
                <div className="agenda-drawer-form-group agenda-create-form-group">
                  <div className="agenda-create-form-header">
                    <div className="agenda-create-form-title-wrap">
                      <span className="agenda-create-form-title">{lang?.create_form || "Create Form"}</span>
                      <LuInfo size={14} color="#141B34" title={lang?.add_registration_questions_desc || "Add registration questions for attendees"} />
                    </div>

                    <button
                      type="button"
                      className="agenda-create-form-add-btn"
                      onClick={() => setAddQuestionModalOpen(true)}
                    >
                      <LuPlus size={14} color="#00BADA" />
                      <span>{lang?.add_question || "Add Question"}</span>
                    </button>
                  </div>

                  {/* Questions List or Empty State */}
                  {questions.length > 0 ? (
                    <div className="agenda-q-list">
                      {questions.map((q, idx) => {
                        const text = typeof q === "string" ? q : q.question;
                        const type = typeof q === "object" && q.type ? q.type : "Short Answer";
                        const isMenuOpen = activeQuestionMenuIndex === idx;
                        const isDraggingCurrent = draggedQuestionIndex === idx;
                        const isDragTarget = dragOverQuestionIndex === idx && draggedQuestionIndex !== idx;

                        return (
                          <div
                            key={idx}
                            className={`agenda-q-card ${isMenuOpen ? "menu-active" : ""} ${isDraggingCurrent ? "is-dragging" : ""
                              } ${isDragTarget ? "drag-target" : ""}`}
                            draggable
                            onDragStart={(e) => handleQuestionDragStart(e, idx)}
                            onDragOver={(e) => handleQuestionDragOver(e, idx)}
                            onDrop={(e) => handleQuestionDrop(e, idx)}
                            onDragEnd={handleQuestionDragEnd}
                          >
                            <div className="agenda-q-card-inner">
                              <div className="agenda-q-card-left">
                                {/* Drag Handle */}
                                <div className="agenda-q-drag-handle" title={lang?.drag_to_reorder || "Drag to reorder"}>
                                  <LuGripVertical size={18} color="#818181" />
                                </div>

                                {/* Question Type Tag */}
                                <div className="agenda-q-tag">
                                  <span>{getQuestionTypeLabel(type, lang)}</span>
                                </div>

                                {/* Question Text */}
                                <span className="agenda-q-text" title={text}>
                                  {text}
                                </span>
                              </div>

                              {/* Meatballs Menu Button */}
                              <div className="agenda-q-menu-wrap">
                                <button
                                  type="button"
                                  className={`agenda-q-meatballs-btn ${isMenuOpen ? "active" : ""}`}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setActiveQuestionMenuIndex((prev) => (prev === idx ? null : idx));
                                  }}
                                  title="Options"
                                >
                                  <LuMoreVertical size={18} color="#4F4F4F" />
                                </button>

                                {isMenuOpen && (
                                  <div
                                    className="agenda-q-menu-dropdown"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                    }}
                                  >
                                    <button
                                      type="button"
                                      className="agenda-q-menu-item"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setEditingQuestionIndex(idx);
                                        setAddQuestionModalOpen(true);
                                        setActiveQuestionMenuIndex(null);
                                      }}
                                    >
                                      <LuPencil size={14} color="#00BADA" />
                                      <span>{lang?.edit || "Edit"}</span>
                                    </button>
                                    <button
                                      type="button"
                                      className="agenda-q-menu-item delete"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleRemoveQuestion(idx);
                                      }}
                                    >
                                      <LuTrash2 size={14} color="#EF4444" />
                                      <span>{lang?.delete || "Delete"}</span>
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div
                      className="agenda-drawer-questions-box"
                      style={{ cursor: "pointer" }}
                      onClick={() => setAddQuestionModalOpen(true)}
                    >
                      <div
                        style={{
                          width: "30px",
                          height: "30px",
                          borderRadius: "50%",
                          background: "#F1F5F9",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#4F4F4F",
                        }}
                      >
                        <LuPlus size={16} />
                      </div>
                      <span style={{ fontWeight: "500", fontSize: "14px", color: "#4F4F4F" }}>
                        {lang?.no_questions_added || "No questions have been added."}
                      </span>
                      <span style={{ fontSize: "12px", color: "rgba(79, 79, 79, 0.6)" }}>
                        {lang?.click_to_add_questions_desc || "Click to add questions you plan to ask the attendees."}
                      </span>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* =========================================================================
                FIELDS: WEBINAR (Matches WebinarTab)
                ========================================================================= */}
            {scheduleType === "webinar" && (
              <>
                {/* Row 1: Webinar Title (Full Width) */}
                <div className="agenda-drawer-form-group">
                  <label className="agenda-drawer-label">
                    {lang?.webinar_title || "Webinar Title"} <span className="agenda-drawer-req-star">*</span>
                  </label>
                  <div className={`agenda-drawer-input-box ${errors.title ? "has-error" : ""}`}>
                    <input
                      type="text"
                      name="title"
                      className="agenda-drawer-input"
                      placeholder={lang?.webinar_title_placeholder || "e.g., Advanced Cataract Surgical Techniques"}
                      value={formData.title}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </div>
                </div>

                {/* Row 2: Medical Subspecialty Dropdown (Full Width / One Line) */}
                <div className="agenda-drawer-form-group" ref={categoryRef}>
                  <label className="agenda-drawer-label">
                    {lang?.medical_subspecialty || "Medical Subspecialty"} <span className="agenda-drawer-req-star">*</span>
                  </label>
                  <div
                    className={`agenda-drawer-input-box ${errors.category ? "has-error" : ""}`}
                    style={{ cursor: "pointer", justifyContent: "space-between" }}
                    onClick={() => setCategoryOpen(!categoryOpen)}
                  >
                    <span
                      style={{
                        fontSize: "14px",
                        color: formData.category ? "#031316" : "#8F98A3",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {formData.category || (lang?.select_medical_field || "Select medical field")}
                    </span>
                    <LuChevronDown size={16} color="#031316" />
                  </div>

                  {categoryOpen && (
                    <div className="agenda-drawer-dropdown-menu">
                      {categoryOptions.map((cat) => (
                        <div
                          key={cat}
                          className={`agenda-drawer-dropdown-item ${formData.category === cat ? "selected" : ""}`}
                          onClick={() => {
                            setFormData((p) => ({ ...p, category: cat }));
                            setCategoryOpen(false);
                            if (errors.category) setErrors((p) => ({ ...p, category: "" }));
                          }}
                        >
                          <span>{cat}</span>
                          {formData.category === cat && <LuCheck size={14} color="#00BADA" />}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Row 3: Date & Start Time */}
                <div className="agenda-drawer-form-row">
                  <div className="agenda-drawer-form-group">
                    <label className="agenda-drawer-label">
                      {lang?.date || "Date"} <span className="agenda-drawer-req-star">*</span>
                    </label>
                    <ModernDatePicker
                      value={formData.startDate}
                      minDate={moment().startOf("day")}
                      onChange={(val) => handleDateChange(val, "startDate")}
                      error={Boolean(errors.startDate)}
                      placeholder="YYYY-MM-DD"
                    />
                  </div>

                  <div className="agenda-drawer-form-group">
                    <label className="agenda-drawer-label">
                      {lang?.start_time || "Start Time"} <span className="agenda-drawer-req-star">*</span>
                    </label>
                    <ModernTimePicker
                      value={formData.startTime}
                      minTime={minStartTime}
                      disabled={!formData.startDate}
                      onDisabledClick={() => handleDisabledFieldClick("start_time")}
                      onChange={(val) => handleTimeChange(val, "startTime")}
                      error={Boolean(errors.startTime)}
                      placeholder="hh:mm A"
                      relatedDate={formData.startDate}
                    />
                  </div>
                </div>

                {/* Row 4: Session Duration & Audience Access */}
                <div className="agenda-drawer-form-row">
                  <div className="agenda-drawer-form-group">
                    <div className="d-flex align-items-center justify-content-between">
                      <label className="agenda-drawer-label">
                        {lang?.session_duration || "Session Duration"} <span className="agenda-drawer-req-star">*</span>
                      </label>
                    </div>
                    <div className={`agenda-drawer-input-box ${errors.duration ? "has-error" : ""}`}>
                      <input
                        type="number"
                        name="duration"
                        className="agenda-drawer-input"
                        placeholder={lang?.min_15 || "min. 15"}
                        min="15"
                        max="120"
                        value={formData.duration}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      <span className="agenda-drawer-badge-suffix">{lang?.duration_suffix_min || "Min"}</span>
                    </div>
                  </div>

                  <div className="agenda-drawer-form-group">
                    <label className="agenda-drawer-label">{lang?.audience_access || "Audience Access"}</label>
                    <div className="agenda-drawer-segment-group">
                      <button
                        type="button"
                        className={`agenda-drawer-segment-btn ${formData.visibility === "Public" ? "active" : ""}`}
                        onClick={() => setFormData((p) => ({ ...p, visibility: "Public" }))}
                      >
                        <LuGlobe size={14} />
                        <span>{lang?.public || "Public"}</span>
                      </button>
                      <button
                        type="button"
                        className={`agenda-drawer-segment-btn ${formData.visibility === "Private" ? "active" : ""}`}
                        onClick={() => setFormData((p) => ({ ...p, visibility: "Private" }))}
                      >
                        <LuLock size={14} />
                        <span>{lang?.private || "Private"}</span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Row 5: Webinar Agenda & Clinical Objectives */}
                <div className="agenda-drawer-form-group">
                  <label className="agenda-drawer-label">
                    {lang?.webinar_agenda_objectives || "Webinar Agenda & Clinical Objectives"} <span className="agenda-drawer-req-star">*</span>
                  </label>
                  <textarea
                    name="description"
                    className={`agenda-drawer-textarea ${errors.description ? "has-error" : ""}`}
                    placeholder={lang?.webinar_desc_placeholder || "Outline the case studies, keynote speakers, and learning objectives..."}
                    value={formData.description}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </div>
              </>
            )}

            {/* =========================================================================
                FIELDS: CLINICAL SESSION (Matches ClinicalSessionTab)
                ========================================================================= */}
            {scheduleType === "clinical_session" && (
              <>
                {/* Row 1: Session Topic (Full Width) */}
                <div className="agenda-drawer-form-group">
                  <label className="agenda-drawer-label">
                    {lang?.session_topic || "Session Topic"} <span className="agenda-drawer-req-star">*</span>
                  </label>
                  <div className={`agenda-drawer-input-box ${errors.title ? "has-error" : ""}`}>
                    <input
                      type="text"
                      name="title"
                      className="agenda-drawer-input"
                      placeholder={lang?.session_topic_placeholder || "e.g., Complex Retinal Detachment Case Reviews"}
                      value={formData.title}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </div>
                </div>

                {/* Row 2: Medical Subspecialty Dropdown (Full Width / One Line) */}
                <div className="agenda-drawer-form-group" ref={categoryRef}>
                  <label className="agenda-drawer-label">
                    {lang?.medical_subspecialty || "Medical Subspecialty"} <span className="agenda-drawer-req-star">*</span>
                  </label>
                  <div
                    className={`agenda-drawer-input-box ${errors.category ? "has-error" : ""}`}
                    style={{ cursor: "pointer", justifyContent: "space-between" }}
                    onClick={() => setCategoryOpen(!categoryOpen)}
                  >
                    <span
                      style={{
                        fontSize: "14px",
                        color: formData.category ? "#031316" : "#8F98A3",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {formData.category || (lang?.select_medical_field || "Select medical field")}
                    </span>
                    <LuChevronDown size={16} color="#031316" />
                  </div>

                  {categoryOpen && (
                    <div className="agenda-drawer-dropdown-menu">
                      {categoryOptions.map((cat) => (
                        <div
                          key={cat}
                          className={`agenda-drawer-dropdown-item ${formData.category === cat ? "selected" : ""}`}
                          onClick={() => {
                            setFormData((p) => ({ ...p, category: cat }));
                            setCategoryOpen(false);
                            if (errors.category) setErrors((p) => ({ ...p, category: "" }));
                          }}
                        >
                          <span>{cat}</span>
                          {formData.category === cat && <LuCheck size={14} color="#00BADA" />}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Row 3: Date & Start Time */}
                <div className="agenda-drawer-form-row">
                  <div className="agenda-drawer-form-group">
                    <label className="agenda-drawer-label">
                      {lang?.date || "Date"} <span className="agenda-drawer-req-star">*</span>
                    </label>
                    <ModernDatePicker
                      value={formData.startDate}
                      minDate={moment().startOf("day")}
                      onChange={(val) => handleDateChange(val, "startDate")}
                      error={Boolean(errors.startDate)}
                      placeholder="YYYY-MM-DD"
                    />
                  </div>

                  <div className="agenda-drawer-form-group">
                    <label className="agenda-drawer-label">
                      {lang?.start_time || "Start Time"} <span className="agenda-drawer-req-star">*</span>
                    </label>
                    <ModernTimePicker
                      value={formData.startTime}
                      minTime={minStartTime}
                      disabled={!formData.startDate}
                      onDisabledClick={() => handleDisabledFieldClick("start_time")}
                      onChange={(val) => handleTimeChange(val, "startTime")}
                      error={Boolean(errors.startTime)}
                      placeholder="hh:mm A"
                      relatedDate={formData.startDate}
                    />
                  </div>
                </div>

                {/* Row 4: Session Duration & Recurrence Pattern */}
                <div className="agenda-drawer-form-row">
                  <div className="agenda-drawer-form-group">
                    <div className="d-flex align-items-center justify-content-between">
                      <label className="agenda-drawer-label">
                        {lang?.session_duration || "Session Duration"} <span className="agenda-drawer-req-star">*</span>
                      </label>
                    </div>
                    <div className={`agenda-drawer-input-box ${errors.duration ? "has-error" : ""}`}>
                      <input
                        type="number"
                        name="duration"
                        className="agenda-drawer-input"
                        placeholder={lang?.min_15 || "min. 15"}
                        min="15"
                        max="120"
                        value={formData.duration}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      <span className="agenda-drawer-badge-suffix">{lang?.duration_suffix_min || "Min"}</span>
                    </div>
                  </div>

                  <div className="agenda-drawer-form-group">
                    <label className="agenda-drawer-label">{lang?.recurrence_pattern || "Recurrence Pattern"}</label>
                    <div className="agenda-drawer-segment-group">
                      {RECURRENCE_OPTIONS.map((item) => (
                        <button
                          key={item}
                          type="button"
                          className={`agenda-drawer-segment-btn ${formData.recurrence === item ? "active" : ""}`}
                          onClick={() => setFormData((p) => ({ ...p, recurrence: item }))}
                        >
                          <LuRepeat size={12} />
                          <span>
                            {item === "One Time"
                              ? (lang?.one_time || "One Time")
                              : item === "Daily"
                                ? (lang?.daily || "Daily")
                                : item === "Weekly"
                                  ? (lang?.weekly || "Weekly")
                                  : item}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Row 5: Case Discussion Agenda & Objectives */}
                <div className="agenda-drawer-form-group">
                  <label className="agenda-drawer-label">
                    {lang?.case_discussion_agenda_objectives || "Case Discussion Agenda & Objectives"} <span className="agenda-drawer-req-star">*</span>
                  </label>
                  <textarea
                    name="description"
                    className={`agenda-drawer-textarea ${errors.description ? "has-error" : ""}`}
                    placeholder={lang?.clinical_session_desc_placeholder || "Describe the clinical cases to be presented, imaging needed, and discussion points..."}
                    value={formData.description}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </div>
              </>
            )}

          </div>

          {/* Sticky Bottom Action Bar */}
          <div className="agenda-drawer-footer">
            <button
              type="submit"
              className={`agenda-drawer-submit-btn ${!isFormValid() || isSubmitting || isLoadingDetails ? "disabled-style" : ""}`}
              disabled={!isFormValid() || isSubmitting || isLoadingDetails}
            >
              {isSubmitting ? (
                <span style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
                  <LuLoader2 className="agenda-spin-anim" size={18} />
                  <span>{editEvent ? (lang?.updating || "Updating...") : (lang?.processing || "Processing...")}</span>
                </span>
              ) : (
                editEvent ? (lang?.save_changes || "Save Changes") : getFormTitle()
              )}
            </button>
          </div>
        </form>
      </div>

      {/* 16:9 Image Crop Modal */}
      <ImageCropModal
        isOpen={cropModalOpen}
        toggle={() => setCropModalOpen(false)}
        imageSrc={cropImageSrc}
        aspect={16 / 9}
        onCropComplete={handleCropComplete}
      />

      {/* Full Banner Preview Modal */}
      <ImagePreviewModal
        isOpen={isPreviewOpen}
        toggle={togglePreview}
        imageSrc={resolveAgendaImageUrl(bannerPreview, "")}
        alt="Banner Preview"
      />

      {/* Google Places & Map Location Modal */}
      {locationModalOpen && (
        <ModalAddLocation
          isOpen={locationModalOpen}
          userCoords={userCoords}
          locationData={locationData}
          onClose={() => setLocationModalOpen(false)}
          onSave={handleSaveLocation}
        />
      )}

      {/* Add Question Modal (Figma Spec) */}
      {addQuestionModalOpen && (
        <Suspense fallback={null}>
          <AddQuestionModal
            isOpen={addQuestionModalOpen}
            toggle={() => {
              setAddQuestionModalOpen(false);
              setEditingQuestionIndex(null);
            }}
            onAddQuestion={handleAddQuestion}
            initialQuestion={
              editingQuestionIndex !== null ? questions[editingQuestionIndex] : null
            }
          />
        </Suspense>
      )}

    </>
  );
}
